// Slice 7 (introduced) / slice 8 (de-nested): unified "Dealers
// attached to <plant>" box behind a single
// All | App-Using | Non-App Using slider. Lives at the bottom of the
// combined Plants column (under the Unattached count), tracks the
// currently-selected plant from useManufacturingPlantDealersTab, and
// surfaces the primary CTAs (+Attach existing dealer, +Add non-app
// location) in the tab strip's right slot — slice 8 collapsed the
// previous triple-nested boxes (outer SectionCard +
// ManufacturingTabbedSection + inner SectionCards) into a single
// ManufacturingTabbedSection so the dealer rows sit directly inside
// one card and the padding budget shrinks accordingly.
//
// Routing rules:
//   - real plant selected → CTAs visible, slider drives section visibility
//   - UNATTACHED pseudo-row → +Attach hidden (no plant to attach to);
//     +Add non-app still works (unassigned-by-design), and the
//     subscriber rows expose a per-row Set primary plant dropdown
//   - nothing selected → host suppresses this box; we never render
//     here without `isRealPlant || isUnattached`
import { ManufacturingPlantAttachDealerPicker } from "./ManufacturingPlantAttachDealerPicker";
import { ManufacturingPlantDealersLocationsSection } from "./ManufacturingPlantDealersLocationsSection";
import { ManufacturingPlantDealersSubscriberSection } from "./ManufacturingPlantDealersSubscriberSection";
import { ManufacturingTabbedSection, type TabbedSectionTab } from "./ManufacturingTabbedSection";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { UseManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";

// Slice 9: Unattached is now the 4th tab here (was its own
// standalone amber button below the plants list). The tab uses
// tone="warning" so it stays amber even when inactive — the
// "dealers without a primary plant" alert is the highest-priority
// signal in the column. Selecting this tab forces the plant
// selection upstream to UNATTACHED_PLANT_SENTINEL so the underlying
// hook returns the unassigned bucket.
export type DealersAttachedMode = "all" | "app_using" | "non_app" | "unattached";

export function ManufacturingPlantDealersAttachedBox({
  m,
  activePlantLabel,
  mode,
  onModeChange,
  unattachedCount,
}: {
  m: UseManufacturingPlantDealersTab;
  activePlantLabel: string;
  mode: DealersAttachedMode;
  onModeChange: (next: DealersAttachedMode) => void;
  /** Total unattached dealers (subscriber + non-app combined). Used
   *  to label the Unattached tab so the alert count stays visible
   *  even when another tab is active. Computed once at the column
   *  level since the hook only sees the scoped (filtered) lists. */
  unattachedCount: number;
}) {
  const subCount = m.scopedSubscribers.length;
  const locCount = m.scopedLocations.length;
  const allCount = subCount + locCount;

  const headerCtas = (
    <div className="flex flex-wrap gap-2">
      {m.isRealPlant ? (
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          onClick={m.openAttachPicker}
          disabled={m.actionBusy}
        >
          + Attach existing dealer
        </button>
      ) : null}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={m.startCreateLocation}
        disabled={m.actionBusy || m.creatingLocation}
        title={
          m.isUnattached
            ? "Create a non-app dealer location without attaching it to a plant"
            : "Create a non-app dealer location attached to this plant"
        }
      >
        + Add non-app dealer location
      </button>
    </div>
  );

  const attachedDescription = `Dealers attached to ${activePlantLabel || "—"}. Detach a row to clear its primary plant — the dealer record stays.`;
  const unattachedDescription =
    "Dealers without a primary plant. Use the per-row Set primary plant dropdown (App-Using rows) or pick a plant first then Attach (Non-App rows).";

  const tabs: ReadonlyArray<TabbedSectionTab<DealersAttachedMode>> = [
    {
      id: "all",
      label: allCount > 0 ? `All (${allCount})` : "All",
      description: attachedDescription,
      rightSlot: headerCtas,
    },
    {
      id: "app_using",
      label: subCount > 0 ? `App-Using (${subCount})` : "App-Using",
      description: attachedDescription,
      rightSlot: headerCtas,
    },
    {
      id: "non_app",
      label: locCount > 0 ? `Non-App Using (${locCount})` : "Non-App Using",
      description: attachedDescription,
      rightSlot: headerCtas,
    },
    {
      id: "unattached",
      label:
        unattachedCount > 0 ? `⚠ Unattached (${unattachedCount})` : "Unattached",
      description: unattachedDescription,
      rightSlot: headerCtas,
      tone: "warning",
    },
  ];

  return (
    <>
      <ManufacturingTabbedSection<DealersAttachedMode>
        tabs={tabs}
        activeTab={mode}
        onTabChange={onModeChange}
      >
        {mode === "app_using" ? (
          <ManufacturingPlantDealersSubscriberSection m={m} />
        ) : mode === "non_app" ? (
          <ManufacturingPlantDealersLocationsSection m={m} />
        ) : (
          // both "all" and "unattached" render the combined view —
          // the data filtering already flows from `selection`
          // (real plant id vs. UNATTACHED_PLANT_SENTINEL) at the
          // hook level, so the body is the same; only the
          // description / tone / count differ between the two tabs.
          <div className="space-y-4">
            <ManufacturingPlantDealersSubscriberSection m={m} />
            <ManufacturingPlantDealersLocationsSection m={m} />
          </div>
        )}
      </ManufacturingTabbedSection>

      {m.attachPickerOpen && m.isRealPlant ? (
        <ManufacturingPlantAttachDealerPicker
          m={m}
          activePlantLabel={activePlantLabel}
          onClose={m.closeAttachPicker}
        />
      ) : null}
    </>
  );
}
